import { hasValue } from '@amsanalytics/react-shared-library';
import { IApplicationLeaseQuote } from '../Interfaces/Application/IApplicationLeaseQuote';
import { ILeaseLoanClass } from '../Interfaces/Common/ILeaseLoanClass';
import { LeaseCalcMode } from '../Interfaces/Application/ApplicationLeaseCalculationResult';

export interface LeaseQuoteValidationContext {
  /** The quote's loan class from the lookups, if it is still listed. */
  loanClass: ILeaseLoanClass | undefined;
  hasVehicle: boolean;
  mode: LeaseCalcMode;
}

const DEFERRED_SLOTS = [1, 2, 3, 4, 5, 6] as const;

const deferredAmount = (quote: IApplicationLeaseQuote, slot: number): number | null | undefined =>
  quote[`deferredPmtAmount${slot}`];

const deferredDate = (quote: IApplicationLeaseQuote, slot: number): Date | string | null | undefined =>
  quote[`deferredPmtDate${slot}`];

/** The checks the page runs before asking the server to solve; the server checks the rest. */
export const validateLeaseQuote = (quote: IApplicationLeaseQuote, context: LeaseQuoteValidationContext): string[] => {
  const errors: string[] = [];
  const { loanClass } = context;

  if (!quote.leaseLoanClassId) {
    errors.push('A loan class must be selected');
  } else if (loanClass) {
    if (quote.apr < loanClass.minApr || quote.apr > loanClass.maxApr) {
      errors.push(`The APR has to be between ${loanClass.minApr} and ${loanClass.maxApr}`);
    }
    if (hasValue(loanClass.deletedDate)) {
      errors.push('The selected loan class is no longer valid. Pick another.');
    }
  }

  if (context.mode === 'RollBack' && quote.totalDownRollback <= 0) {
    errors.push('Total Down Roll Back must be greater than 0');
  }

  if (!context.hasVehicle) {
    errors.push('A vehicle must be selected');
  }

  if (!quote.paymentFrequencyId) {
    errors.push('Select a payment frequency');
  }

  if (quote.numberOfPmts <= 0) {
    errors.push('Number of Payments must be greater than 0');
  }

  // Cash Down is not compared with the deferred down payments or the deposits received: a Roll Back can work out
  // any Cash Down, and posting checks the money received against Total Down less deferred down (LOS card down
  // payments B1, B6).
  DEFERRED_SLOTS.forEach((slot) => {
    const amount = deferredAmount(quote, slot);
    const date = deferredDate(quote, slot);
    if ((amount ?? 0) > 0 && !hasValue(date)) {
      errors.push(`Add a date for Deferred Payment ${slot} or remove the payment amount`);
    }
    if (!hasValue(amount) && hasValue(date)) {
      errors.push(`Add a payment amount for Deferred Payment ${slot} or remove the date`);
    }
  });

  return errors;
};
